import { useCallback, useEffect } from "react";
import { appId } from "../appConfig";

export type UseReloadProps = {
  maxAttempts?: number;
  timeWindowMs?: number;
  onMaxAttemptsReached?: () => void;
  resetOnUnmount?: boolean;
};

export function useReload(key: string, options: UseReloadProps = {}) {
  const {
    maxAttempts = 3,
    timeWindowMs = 10000,
    onMaxAttemptsReached,
    resetOnUnmount = false,
  } = options;

  const attemptsKey = `${appId}_reload_attempts_${key}`;
  const timestampKey = `${appId}_reload_timestamp_${key}`;

  const resetCounter = useCallback(() => {
    sessionStorage.removeItem(attemptsKey);
    sessionStorage.removeItem(timestampKey);
  }, [attemptsKey, timestampKey]);

  const reloadWithCircuitBreaker = useCallback(() => {
    const now = Date.now();
    const attemptCount = parseInt(sessionStorage.getItem(attemptsKey) || "0");
    const timestamp = parseInt(sessionStorage.getItem(timestampKey) || "0");

    // Reset if outside time window
    if (now - timestamp > timeWindowMs) {
      sessionStorage.setItem(attemptsKey, "1");
      sessionStorage.setItem(timestampKey, now.toString());
      console.log(`Reloading ${key}`);
      window.location.reload();
      return;
    }

    // Check if we've hit the limit
    if (attemptCount >= maxAttempts) {
      console.warn(`Maximum reload attempts (${maxAttempts}) reached for "${key}"`);
      if (onMaxAttemptsReached) {
        onMaxAttemptsReached();
      }
      return;
    }

    // Increment counter and reload
    sessionStorage.setItem(attemptsKey, (attemptCount + 1).toString());
    sessionStorage.setItem(timestampKey, now.toString());

    console.log(`Reloading ${key}`);
    window.location.reload();
  }, [
    attemptsKey,
    timestampKey,
    maxAttempts,
    timeWindowMs,
    onMaxAttemptsReached,
    resetCounter,
    key,
  ]);

  useEffect(() => {
    return () => {
      if (resetOnUnmount) {
        resetCounter();
      }
    };
  }, [resetOnUnmount, resetCounter]);

  return {
    reload: reloadWithCircuitBreaker,
    resetCounter,
    getAttemptCount: () => parseInt(sessionStorage.getItem(attemptsKey) || "0"),
  };
}
